import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

// Define an interface for what you want available from the UserContext.
interface UserContextProps {
	user: any; // You can type this more strictly if you have a user type
	userId: string;
	enterpriseId: string;
	userMetadata: any;
	userAppdata: any;
	isPremium: boolean;
	isEnterpriseStudent: boolean;
	isEnterpriseAdmin: boolean;
	subscriptionStatus: string;
	subscriptionExpiresAt: string;
	subscriptionInterval: string;
}

// Provide some default values.
const defaultUserContext: UserContextProps = {
	user: null,
	userId: '',
	enterpriseId: '',
	userMetadata: null,
	userAppdata: {},
	isPremium: false,
	isEnterpriseStudent: false,
	isEnterpriseAdmin: false,
	subscriptionStatus: 'Free Tier',
	subscriptionExpiresAt: '',
	subscriptionInterval: 'N/A',
};

// Create the context.
const UserContext = createContext<UserContextProps>(defaultUserContext);

// Create a custom hook for easier usage.
export const useUser = () => useContext(UserContext);

// Create the provider.
interface UserProviderProps {
	children: ReactNode;
}


export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
	const { user } = useAuth0();
	const [userId, setUserId] = useState<string>('');
	const [enterpriseId, setEnterpriseId] = useState<string>('');
	const [isEnterpriseStudent, setIsEnterpriseStudent] = useState<boolean>(false);
	const [isEnterpriseAdmin, setIsEnterpriseAdmin] = useState<boolean>(false);
	const [userMetadata, setUserMetadata] = useState<any>({});
	const [userAppdata, setUserAppdata] = useState<any>({});
	const [isPremium, setIsPremium] = useState<boolean>(false);
	const [subscriptionStatus, setSubscriptionStatus] = useState<string>('Free Tier');
	const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState<string>('');
	const [subscriptionInterval, setSubscriptionInterval] = useState<string>('N/A');

	const EnterpriseEnabled = process.env.REACT_APP_ENTERPRISE_ENABLED === "true";

	useEffect(() => {
		if (user) {
			// console.log('User data:', user); // Debugging line to check user data
			// Save the basic Auth0 info.
			setUserId(user.sub || '');

			// Pull metadata (for things like first name, last name, etc.)
			const metadata = user['https://gradly-authentication-server/user_metadata'] || {};
			setUserMetadata(metadata);

			// Pull app metadata (for things like subscription details).
			const appdata = user['https://gradly-authentication-server/app_metadata'] || {};
			setUserAppdata(appdata);

			// Get subscription info from appdata.
			const { subscription_expires_at, cancel_at_period_end, interval } = appdata || {};
			setSubscriptionExpiresAt(subscription_expires_at || '');

			// Compute a friendly interval string.
			if (interval === 'month') {
				setSubscriptionInterval('Monthly');
			} else if (interval === 'annual') {
				setSubscriptionInterval('Annually');
			} else {
				setSubscriptionInterval(interval || 'N/A');
			}

			// Compute isPremium & subscriptionStatus based on expiration.
			const now = new Date();
			let periodEndDate: Date | null = null;
			if (subscription_expires_at) {
				// If it's a number, assume it’s in seconds and convert.
				periodEndDate = typeof subscription_expires_at === 'number'
					? new Date(subscription_expires_at * 1000)
					: new Date(subscription_expires_at);
			}

			const periodEndTime = periodEndDate ? periodEndDate.getTime() : 0;
			if (!subscription_expires_at || periodEndTime < now.getTime()) {
				// User's subscription has expired or is missing.
				setSubscriptionStatus('Free Tier');
				setIsPremium(false);
			} else if (periodEndDate && cancel_at_period_end) {
				// Subscription is active but scheduled for cancellation.
				setSubscriptionStatus('Cancelled');
				setIsPremium(true);
			} else if (periodEndDate) {
				// Subscription is active.
				setSubscriptionStatus('Gradly Pro');
				setIsPremium(true);
			} else {
				setSubscriptionStatus('Unknown');
				setIsPremium(false);
			}
			// Now check enterprise validation: if they’re an enterprise student,
			// override and treat them as “Pro”
			// We fire this *after* our subscription check, but only once per login.
			(async () => {
				try {
					// Check if the feature flag for enterprise validation is enabled
					const res = await axios.post(`${process.env.REACT_APP_API_SERVER_URL}/api/enterprise-validation`, {
						userId: user.sub,
						userEmail: user.email,
					});
					// console.log('Enterprise validation response:', res.data); // Debugging line to check response data
					const { enterpriseStudent, enterpriseAdmin, enterpriseId } = res.data;
					if (enterpriseStudent) {
						setIsPremium(true);
						setEnterpriseId(enterpriseId);
						setIsEnterpriseStudent(true);
						setSubscriptionStatus('Gradly Pro');
					}
					if (enterpriseAdmin) {
						setIsPremium(true);
						setEnterpriseId(enterpriseId);
						setIsEnterpriseAdmin(true);
						setSubscriptionStatus('Gradly Pro');
					}
				} catch (err) {
					console.error('Enterprise validation failed', err);
				}
			})();
		}
	}, [user]);

	return (
		<UserContext.Provider
			value={{
				user,
				userId,
				enterpriseId,
				userMetadata,
				userAppdata,
				isEnterpriseStudent,
				isEnterpriseAdmin,
				isPremium,
				subscriptionStatus,
				subscriptionExpiresAt,
				subscriptionInterval,
			}}
		>
			{children}
		</UserContext.Provider>
	);
};

export default UserContext;